"use client";

import Image from "next/image";
import { useTheme } from "next-themes";
import { useSyncExternalStore } from "react";
import { Link } from "@/i18n/navigation";
import { LOGOMARK_INTRINSIC, logomark } from "@/config/logo";

type LogomarkProps = {
  size?: "nav" | "footer";
  asLink?: boolean;
};

/* Same rendered heights as Wordmark so the header does not shift. Width follows the 88:60 box. */
const sizeClass = {
  nav: "h-9 w-auto md:h-10",
  footer: "h-8 w-auto sm:h-9",
} as const;

const emptySubscribe = () => () => {};

/**
 * Folded W logomark. Swaps the file by theme like Wordmark does: `html.dark img` is
 * counter-inverted in globals.css, so each file carries its final colours.
 */
export function Logomark({ size = "nav", asLink = true }: LogomarkProps) {
  const { resolvedTheme } = useTheme();
  const mounted = useSyncExternalStore(emptySubscribe, () => true, () => false);

  const isDark = mounted && resolvedTheme === "dark";
  const src = isDark ? logomark.dark : logomark.light;
  const isNav = size === "nav";

  const img = (
    <span className="inline-flex shrink-0 items-center leading-none">
      <Image
        src={src}
        alt="Walter Media"
        width={LOGOMARK_INTRINSIC.width}
        height={LOGOMARK_INTRINSIC.height}
        className={`block ${sizeClass[size]} object-contain object-left`}
        priority={isNav}
        fetchPriority={isNav ? "high" : undefined}
        unoptimized
      />
    </span>
  );

  if (asLink) {
    return (
      <Link
        href="/"
        aria-label="Walter Media"
        className="inline-flex shrink-0 items-center leading-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]"
      >
        {img}
      </Link>
    );
  }

  return img;
}
